import React, { useState, useEffect, FC, useMemo, memo } from "react";
import {
  HeaderSection,
  ModalContainer,
  Heading,
  CloseButton,
  SubHeaderSection,
  Offer,
  EnableCTA,
  DismissCTA,
  Overlay,
  SubHeading,
  Text,
  SubText
} from "./EnablePushNotificationModal.css";
import { CloseIconWMO } from "@usap/uicomponents";
import type { Content, ModalContent } from "./EnablePushNotificationModal.types";
import { getFallbackContent, isFlutterApp } from "./EnablePushNotificationModal.helpers";
import { useCookies } from "react-cookie";
import usePushNotificationOptIn from "../../../hooks/usePushNotificationOptIn";
import { GTM } from '../../../helpers'

interface Props {
  content: Content;
}

const EnablePushNotificationModal: FC<Props> = ({ content = {} }) => {
  const [cookies, setCookie] = useCookies([
    "enablePushNotificationModalClosed",
  ]);
  const [isVisible, setIsVisible] = useState(false);
  const optInFlag = usePushNotificationOptIn();

  const { closePNOptinCTAClick, enableNotificationsPNOptinCTAClick, notNowPNOptinCTAClick } = GTM

  const modalContent: ModalContent = useMemo(() => {
    // If f44 flag value is b, show version 2
    if(optInFlag) {
      return content?.version2 || getFallbackContent(optInFlag);
    }
    return content?.version1 || getFallbackContent(optInFlag);
  }, [content, optInFlag]);

  const setCookieOnBannerClose = () => {
    const cookieMaxAge = 24 * 60 * 60; // 1 day
    setCookie("enablePushNotificationModalClosed", "true", {
      maxAge: cookieMaxAge,
      path: "/",
    });
  };

  const onCloseHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setIsVisible(false);
    setCookieOnBannerClose();
    const source = e.currentTarget.getAttribute("data-source");
    if(source === "not_now_cta") {
      notNowPNOptinCTAClick(modalContent.id);
    } else {
      closePNOptinCTAClick(modalContent.id);
    }
  };

  const onEnableHandler = () => {
    if (isFlutterApp() && window.flutter_inappwebview?.callHandler) {
      enableNotificationsPNOptinCTAClick(modalContent.id);
      window.flutter_inappwebview.callHandler("openAppSettings", {});
      setTimeout(() => {
        setIsVisible(false);
        setCookieOnBannerClose();
      }, 500);
    }
  };

  useEffect(() => {
    if (
      isFlutterApp() &&
      window.isPushNotificationDenied === 'true' &&
      !cookies?.enablePushNotificationModalClosed
    ) {
      setIsVisible(true);
    }
  }, [cookies?.enablePushNotificationModalClosed]);

  if (!isVisible) return null;

  return (
    <>
      <ModalContainer>
        <HeaderSection>
          <Heading>{modalContent.mainHeader}</Heading>
          <CloseButton type="button" data-source="close_cta" onClick={onCloseHandler}>
            <CloseIconWMO />
          </CloseButton>
        </HeaderSection>
        <SubHeaderSection>
          <SubHeading>{modalContent.subHeader}</SubHeading>
          {modalContent.offers.map((data) => {
            return (
              <Offer key={data.id}>
                <Text>{data?.strongtext && <strong>{data.strongtext}</strong>} {data.text}</Text>
                {data?.subtext && <SubText>{data.subtext}</SubText>}
              </Offer>
            );
          })}
        </SubHeaderSection>
        <EnableCTA type="button" onClick={onEnableHandler}>
          {modalContent.buttons.enableButtonText}
        </EnableCTA>
        <DismissCTA type="button" data-source="not_now_cta" onClick={onCloseHandler}>
          {modalContent.buttons.dismissButtonText}
        </DismissCTA>
      </ModalContainer>
      <Overlay />
    </>
  );
};

export default memo(EnablePushNotificationModal);
